import React, { useState } from 'react';
import styles from '../styles/creditCardForm.module.scss';
import { Controller, useForm } from 'react-hook-form';
import MainButton from '@ui-kit/components/mainButton';
import { PurchaseData } from '../model/interfaces';
import ReactFlagsSelect from 'react-flags-select';
import classNames from 'classnames';
import { states } from '@base/utils/states';
import TicketService from '../model/ticketsService';
import { container } from 'tsyringe';
import { CreditCardInput } from '@ui-kit/components/creditCardInput';
import ExpiryDateInput from '@ui-kit/components/expiryDateInput';
import CvcInput from '@ui-kit/components/cvcInput';
import CardType from '@ui-kit/components/cardType';
import FormError from '@ui-kit/components/formError';
import { validateCreditCardNumber, validateExpiryDate } from '@utils/functions';
import { t } from '@base/utils/helpers';
import { IMessage } from '@base/features/message/message';

export default function CreditCardForm(props: { price: string; onFinish: () => void }) {
  const ticketService: TicketService = container.resolve('ticketsService');
  const message: IMessage = container.resolve('message');
  const [apiError, setApiError] = useState('');

  const {
    register,
    handleSubmit,
    control,
    watch,
    formState: { isSubmitting, isValid, isSubmitSuccessful, errors },
  } = useForm({
    mode: 'onChange',
    shouldUnregister: true,
  });

  async function onSubmit(formData: any) {
    const { card_type, first_name, last_name, cvn, address, city, state, zip, country } = formData;
    const [exp_mon, exp_year] = formData.expiry_date.split(' / ');
    const card_number = formData.card_number.replace(/\s/g, '');

    const purchaseData: PurchaseData = {
      amount: props.price,
      first_name,
      last_name,
      card_number,
      exp_mon,
      exp_year: '20' + exp_year,
      cvn,
      address,
      city,
      state,
      zip,
      country,
      card_type,
    };

    try {
      await ticketService.purchase(purchaseData);
      message.success('Purchase has been successfully made');
      props.onFinish();
    } catch (e: any) {
      const msg = e.message ? e.message : 'Unknown error. Please try again.';
      setApiError(msg);
      message.error(msg);
    }
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className={styles.form}>
      <label>
        <span>{t('card')}</span>
        <div className={styles.creditCard}>
          <Controller
            control={control}
            name="card_type"
            render={({ field: { onChange } }) => (
              <CardType cardNumber={watch('card_number')} onChange={onChange} />
            )}
          />
          <Controller
            control={control}
            name="card_number"
            rules={{
              required: t('required'),
              validate: validateCreditCardNumber,
            }}
            render={({ field: { onChange, onBlur } }) => (
              <CreditCardInput onChange={onChange} onBlur={onBlur} />
            )}
          />
          <Controller
            control={control}
            name="expiry_date"
            rules={{ required: t('required'), validate: validateExpiryDate }}
            render={({ field: { onChange, onBlur } }) => (
              <ExpiryDateInput onChange={onChange} onBlur={onBlur} style={{ width: '6rem' }} />
            )}
          />
          <Controller
            control={control}
            name="cvn"
            rules={{
              required: t('required'),
              minLength: { value: 3, message: t('cvc_restrictions') },
            }}
            render={({ field: { onChange, onBlur } }) => (
              <CvcInput onChange={onChange} onBlur={onBlur} style={{ width: '4rem' }} />
            )}
          />
        </div>
      </label>
      {['card_number', 'expiry_date', 'cvn'].filter((key) => key in errors).length ? (
        <FormError
          className={styles.error}
          errors={errors}
          field={['card_number', 'expiry_date', 'cvn'].filter((key) => key in errors)[0]}
        />
      ) : null}
      <label>
        <span>{t('first_name')}</span>
        <input {...register('first_name', { required: t('required') })} autoComplete="cc-given-ame" />
      </label>
      <FormError className={styles.error} errors={errors} field="first_name" />
      <label>
        <span>{t('last_name')}</span>
        <input {...register('last_name', { required: t('required') })} autoComplete="cc-family-name" />
      </label>
      <FormError className={styles.error} errors={errors} field="last_name" />
      <label>
        <span>{t('address')}</span>
        <input {...register('address', { required: t('required') })} />
      </label>
      <FormError className={styles.error} errors={errors} field="address" />
      <Controller
        name="country"
        control={control}
        defaultValue="US"
        render={({ field: { onChange, value } }) => (
          <label>
            <span>{t('country')}</span>
            <ReactFlagsSelect
              className={classNames('country-select', styles.country)}
              selected={value}
              onSelect={onChange}
              searchable
            />
          </label>
        )}
      />
      <FormError className={styles.error} errors={errors} field="country" />
      <label>
        <span>{watch('country') === 'US' ? t('state') : t('province')}</span>
        {watch('country') === 'US' ? (
          <select
            {...register('state', {
              shouldUnregister: true,
              required: t('required'),
            })}
          >
            {states.list().map((state) => (
              <option value={state.code} key={state.code}>
                {state.name}
              </option>
            ))}
          </select>
        ) : (
          <input
            {...register('state', {
              shouldUnregister: true,
              required: t('required'),
            })}
          />
        )}
      </label>
      <FormError className={styles.error} errors={errors} field="state" />
      <label>
        <span>{t('city')}</span>
        <input {...register('city', { required: t('required') })} />
      </label>
      <FormError className={styles.error} errors={errors} field="city" />
      <label>
        <span>{t('zip')}</span>
        <input {...register('zip', { required: t('required') })} autoComplete="postal-code" />
      </label>
      <FormError className={styles.error} errors={errors} field="zip" />
      <MainButton className={styles.button} loading={isSubmitting} disabled={!isValid}>
        {isSubmitSuccessful || apiError ? t('pay_again') : t('pay')}
      </MainButton>
    </form>
  );
}
